import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
  cancelScheduledPdpUiUpdated,
  dispatchPdpUiUpdated,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { submitKlaviyoBackInStockFlow } from '@theme/klaviyo-client-bis';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.target = this.closest('.accessory-product') ? this.closest('.accessory-product') : this.closest('.shopify-section, dialog, product-card');

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable(flag = true) {
    this.refs.addToCartButton.disabled = true;
    this.target?.querySelector('.pdp-sticky-add-to-cart-block button[ref="addToCartButton"]')?.setAttribute('disabled', 'true');
    // if (flag) {
    //   this.closest('product-form-component')?.classList.add('hidden');
    //   this.closest('.buy-buttons-block')?.querySelector('.notify-me-form-wrapper')?.classList.remove('hidden');
    // }
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
    this.target?.querySelector('.pdp-sticky-add-to-cart-block button[ref="addToCartButton"]')?.setAttribute('disabled', 'false');
    // this.closest('product-form-component')?.classList.remove('hidden');
    // this.closest('.buy-buttons-block')?.querySelector('.notify-me-form-wrapper')?.classList.add('hidden');
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal') && !this.closest('.cross-sell__carousel')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    // Skip the "Added" animation for cross-sell carousel buttons — disable instead.
    // Defer to next tick so the form submit still fires with the current click.
    if (this.closest('.cross-sell__carousel')) {
      setTimeout(() => this.disable(), 0);
      return;
    }

    let stickyAddToCartButton = document.querySelector('.pdp-sticky-add-to-cart-block button[ref="addToCartButton"]');
    let pdpButton = this.closest('.buy-buttons-block');

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    if (pdpButton && stickyAddToCartButton && !stickyAddToCartButton.classList.contains('atc-added')) {
      stickyAddToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
        if (pdpButton) stickyAddToCartButton?.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    this.target = this.closest('.accessory-product') ? this.closest('.accessory-product') : this.closest('.shopify-section, dialog, product-card');
    this.target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.addEventListener('click', this.#onPdpBackInStockClick, { signal });
    this.#syncPdpStickyBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);
    const sellType = this.dataset.sellType || 'direct';

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
      formData.append('properties[_sell_type]', sellType);
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */(newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    const source = event.detail.data.html;
    const variant = event.detail.resource;
    const sellingPlanInput = this.querySelector('form')?.querySelector('input[name="selling_plan"]');

    if (variant && sellingPlanInput) {
      const allocation = variant.selling_plan_allocations?.[0];
      sellingPlanInput.value = allocation ? allocation.selling_plan_id : "";
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const target = addToCartButtonContainer?.closest('.accessory-product') ? addToCartButtonContainer.closest('.accessory-product') : addToCartButtonContainer.closest('.shopify-section, dialog, product-card');

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));
    /** @type {HTMLElement | null} */
    let newAddToCartButton = null;
    if (form?.id) {
      newAddToCartButton = /** @type {HTMLElement | null} */ (
        source.getElementById(form.id)?.querySelector('[ref="addToCartButton"]') ?? null
      );
    }
    if (!newAddToCartButton) {
      const buyButtonsBlock = this.closest('.buy-buttons-block');
      const blockScopedClass = buyButtonsBlock
        ? [...buyButtonsBlock.classList].find((c) => c.startsWith('buy-buttons-block--'))
        : null;
      if (blockScopedClass) {
        newAddToCartButton = source.querySelector(`.${CSS.escape(blockScopedClass)} [ref="addToCartButton"]`);
      }
    }
    if (!newAddToCartButton && this.dataset.productId) {
      const sectionId = this.dataset.sectionId;
      const scopedSelector = sectionId
        ? `product-form-component[data-product-id="${CSS.escape(this.dataset.productId)}"][data-section-id="${CSS.escape(sectionId)}"] [ref="addToCartButton"]`
        : `product-form-component[data-product-id="${CSS.escape(this.dataset.productId)}"] [ref="addToCartButton"]`;
      newAddToCartButton = source.querySelector(scopedSelector);
    }
    if (!newAddToCartButton) {
      newAddToCartButton = source.querySelector('[ref="addToCartButton"]');
    }

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || (event.detail.resource.available == false && !variant.selling_plan_allocations?.length)) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      source.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = source.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = source.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          source.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = source.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // update sku and in stock information
    const currentSkuElement = this.target?.querySelector('.product-stock-sku-info-wrapper');
    const newSkuElement = source.querySelector('.product-stock-sku-info-wrapper');
    if (currentSkuElement != null || newSkuElement != null) {
      this.#morphOrUpdateElement(currentSkuElement, newSkuElement);
    }

    // update product title and variant title information
    const currentTitleElement = this.target?.querySelector('.main-product-title-wrapper');
    const newTitleElement = source.querySelector('.main-product-title-wrapper');
    if (currentTitleElement != null || newTitleElement != null) {
      this.#morphOrUpdateElement(currentTitleElement, newTitleElement);
    }

    // update sticky add to cart button if it exists
    const stickyAddToCartButton = this.target?.querySelector('.pdp-sticky-add-to-cart-block');
    const newStickyAddToCartButton = source.querySelector('.pdp-sticky-add-to-cart-block');
    if (stickyAddToCartButton != null || newStickyAddToCartButton != null) {
      this.#morphOrUpdateElement(stickyAddToCartButton, newStickyAddToCartButton);
    }

    const comingSoonForm = this.target?.querySelector('.coming-soon-form-wrapper');
    const sourcePfc = source.querySelector(
      `product-form-component[data-section-id="${this.dataset.sectionId}"]`
    );

    if (sourcePfc) {
      const pfc = this.closest('product-form-component');
      const useCustomComingSoonForm = sourcePfc.dataset.useCustomComingSoonForm === 'true';
      const isComingSoon = sourcePfc.dataset.comingSoonVariant === 'true';
      if (isComingSoon && useCustomComingSoonForm) {
        pfc?.classList.add('hidden');
        comingSoonForm?.classList.remove('hidden');
      } else {
        pfc?.classList.remove('hidden');
        comingSoonForm?.classList.add('hidden');
      }
    }

    if (sourcePfc) {
      if (sourcePfc.dataset.comingSoonVariant != null) {
        this.dataset.comingSoonVariant = sourcePfc.dataset.comingSoonVariant;
      }
      if (sourcePfc.dataset.useCustomComingSoonForm != null) {
        this.dataset.useCustomComingSoonForm = sourcePfc.dataset.useCustomComingSoonForm;
      }
      if (sourcePfc.dataset.notifyVisible != null) {
        this.dataset.notifyVisible = sourcePfc.dataset.notifyVisible;
      }
    }

    const buyButtonsBlock = this.closest('.buy-buttons-block');

    if (buyButtonsBlock) {
      const buyButtonsBlockClass = [...buyButtonsBlock.classList].find((className) =>
        className.startsWith('buy-buttons-block--')
      );
      const sourceBuyButtonsBlock = buyButtonsBlockClass
        ? source.querySelector(`.${CSS.escape(buyButtonsBlockClass)}`)
        : source.querySelector('.buy-buttons-block');

      if (sourceBuyButtonsBlock?.dataset.variantId != null) {
        buyButtonsBlock.dataset.variantId = sourceBuyButtonsBlock.dataset.variantId;
      } else if (event.detail.resource?.id != null) {
        buyButtonsBlock.dataset.variantId = String(event.detail.resource.id);
      }

      if (sourceBuyButtonsBlock?.dataset.stockLevel != null) {
        buyButtonsBlock.dataset.stockLevel = sourceBuyButtonsBlock.dataset.stockLevel;
      }
    }

    const currentNotify = this.querySelector('.pdp-back-in-stock-notify');
    const newNotify = sourcePfc?.querySelector('.pdp-back-in-stock-notify');
    if (currentNotify && newNotify) {
      morph(currentNotify, newNotify);
    }

    // Keep BIS/Klaviyo API attributes in sync (morph can leave stale datasets; fallback when HTML has no notify node).
    if (currentNotify) {
      const variantResourceId = event.detail.resource?.id;
      if (variantResourceId != null) {
        currentNotify.dataset.variantId = String(variantResourceId);
      } else {
        currentNotify.removeAttribute('data-variant-id');
      }
      if (this.dataset.comingSoonVariant != null) {
        currentNotify.dataset.comingSoon = this.dataset.comingSoonVariant === 'true' ? 'true' : 'false';
      } else if (newNotify?.dataset.comingSoon != null) {
        currentNotify.dataset.comingSoon = newNotify.dataset.comingSoon;
      }
    }

    this.#syncPdpStickyBar();

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();

    if (this.#shouldDispatchPdpUiUpdated()) {
      cancelScheduledPdpUiUpdated();
      dispatchPdpUiUpdated({
        source: 'variant-update',
        variantId: event.detail.resource?.id ?? null,
        sectionId: this.dataset.sectionId ?? null,
        productId: this.dataset.productId ?? null,
      });
    }
  };

  /**
   * @returns {boolean}
   */
  #shouldDispatchPdpUiUpdated() {
    const mainVariantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    const productSection = mainVariantPicker?.closest('.shopify-section');

    return productSection != null && productSection.contains(this);
  }

  /**
   * Hides the sticky PDP bar when notify / coming-soon replaces the main buy box.
   */
  #syncPdpStickyBar() {
    const sticky = document.querySelector('.pdp-sticky-add-to-cart-wrapper');
    if (!sticky) return;
    const hide =
      this.dataset.notifyVisible === 'true' ||
      this.dataset.comingSoonVariant === 'true' ||
      this.classList.contains('hidden');
    sticky.classList.toggle('pdp-sticky-add-to-cart-wrapper--back-in-stock-mode', hide);
  }

  /**
   * Klaviyo back-in-stock signup (same payload as cart sold-out notify).
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  #onPdpBackInStockClick = async (event) => {
    const button = event.target.closest('.pdp-back-in-stock-notify__submit');
    if (!button || !this.contains(button)) return;

    const root = this.querySelector('.pdp-back-in-stock-notify');
    const emailInput = root?.querySelector('.pdp-back-in-stock-notify__input');
    const errorEl = root?.querySelector('.pdp-back-in-stock-notify__error');
    const formEl = root?.querySelector('.pdp-back-in-stock-notify__form');
    const messageEl = root?.querySelector('.pdp-back-in-stock-notify__message');
    const successEl = root?.querySelector('.pdp-back-in-stock-notify__success');

    if (!root || !emailInput) return;

    const email = emailInput.value.trim();
    errorEl?.classList.add('hidden');
    if (errorEl) errorEl.textContent = '';

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (errorEl) {
        errorEl.textContent =
          (typeof Theme !== 'undefined' && Theme.translations?.notify_me_incorrect_email) ||
          'Please enter a valid email address';
        errorEl.classList.remove('hidden');
      }
      emailInput.focus();
      return;
    }

    const variantId = root.dataset.variantId || this.refs.variantId?.value;
    const companyId =
      typeof Theme !== 'undefined' && Theme.otterVars && Theme.otterVars.klaviyoApiKey;

    if (!variantId) return;

    if (!companyId) {
      if (errorEl) {
        errorEl.textContent =
          (typeof Theme !== 'undefined' && Theme.translations?.notify_me_error_result) ||
          'Notifications are not configured. Please try again later.';
        errorEl.classList.remove('hidden');
      }
      console.error('Klaviyo public API key not configured');
      return;
    }

    const defaultLabel = button.textContent?.trim() || 'Notify me';
    button.disabled = true;
    button.textContent = 'Sending...';

    const comingSoon =
      root.dataset.comingSoon === 'true' ||
      (root.dataset.comingSoon !== 'false' && this.dataset.comingSoonVariant === 'true');

    const result = await submitKlaviyoBackInStockFlow({
      publicKey: companyId,
      email,
      variantId,
      comingSoon,
    });

    if (result.ok) {
      messageEl?.classList.add('hidden');
      formEl?.classList.add('hidden');
      successEl?.classList.remove('hidden');
    } else {
      if (errorEl && result.errorMessage) {
        errorEl.textContent = result.errorMessage;
        errorEl.classList.remove('hidden');
      }
      button.disabled = false;
      button.textContent = defaultLabel;
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = (event) => {
    event.detail.holder?.querySelector('add-to-cart-component').disable(false);
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
